import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';

interface Inspection {
  qualityInspectionId: string;
  referenceType: string;
  itemNameSnapshot: string;
  sampleSize: number;
  result: number;
  inspectorName: string | null;
  inspectedOn: string;
}

const RESULT_COLOR: Record<number, string> = { 1: 'yellow', 2: 'green', 3: 'red' };

export function QualityPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['quality'] as const,
    queryFn: async () => (await apiClient.get<Inspection[]>('/api/quality/inspections')).data,
  });

  const rLabel = (r: number) => t(`quality.result.${r === 1 ? 'pending' : r === 2 ? 'accepted' : 'rejected'}`);

  return (
    <Stack gap="lg">
      <PageHeader eyebrow={t('nav.sections.operations')} title={t('quality.title')} subtitle={t('quality.subtitle')} />
      <Group justify="flex-end">
        <Button color="wheels" leftSection={<IconPlus size={16} stroke={1.8} />} onClick={() => setCreateOpen(true)}>
          {t('quality.new')}
        </Button>
      </Group>

      <Paper p="md">
        {isLoading ? <Group justify="center" py="lg"><Loader color="wheelsRed" /></Group>
          : (data ?? []).length === 0 ? <Text size="sm" c="dimmed" ta="center" py="lg">{t('quality.empty')}</Text>
          : (
          <Table verticalSpacing="sm">
            <Table.Thead><Table.Tr>
              <Table.Th>{t('quality.item')}</Table.Th>
              <Table.Th>{t('quality.reference')}</Table.Th>
              <Table.Th>{t('quality.sample')}</Table.Th>
              <Table.Th>{t('quality.date')}</Table.Th>
              <Table.Th>{t('quality.resultCol')}</Table.Th>
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {(data ?? []).map((i) => (
                <Table.Tr key={i.qualityInspectionId}>
                  <Table.Td>{i.itemNameSnapshot}</Table.Td>
                  <Table.Td>{i.referenceType}</Table.Td>
                  <Table.Td className="font-numeric">{i.sampleSize}</Table.Td>
                  <Table.Td className="font-numeric">{i.inspectedOn}</Table.Td>
                  <Table.Td><Badge variant="light" color={RESULT_COLOR[i.result]}>{rLabel(i.result)}</Badge></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <CreateModal opened={createOpen} onClose={() => setCreateOpen(false)}
        onSaved={() => void qc.invalidateQueries({ queryKey: ['quality'] })} />
    </Stack>
  );
}

function CreateModal({ opened, onClose, onSaved }: { opened: boolean; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [item, setItem] = useState('');
  const [ref, setRef] = useState('استلام بضاعة');
  const [sample, setSample] = useState<number>(1);
  const [result, setResult] = useState('2');
  const [inspector, setInspector] = useState('');
  const [date, setDate] = useState<Date | null>(new Date());
  const [notes, setNotes] = useState('');

  const save = useMutation({
    mutationFn: async () => apiClient.post('/api/quality/inspections', {
      referenceType: ref.trim(), itemId: null, itemNameSnapshot: item.trim(),
      sampleSize: sample, result: Number(result), inspectorName: inspector.trim() || null,
      inspectedOn: toIso(date!), notes: notes.trim() || null,
    }),
    onSuccess: () => { setItem(''); setInspector(''); setNotes(''); onSaved(); onClose(); },
    onError: () => notifications.show({ color: 'red', title: t('common.toasts.errorTitle'), message: '' }),
  });

  return (
    <Modal opened={opened} onClose={onClose} title={t('quality.new')}>
      <Stack gap="sm">
        <TextInput label={t('quality.item')} value={item} onChange={(e) => setItem(e.currentTarget.value)} withAsterisk />
        <Group grow>
          <TextInput label={t('quality.reference')} value={ref} onChange={(e) => setRef(e.currentTarget.value)} />
          <NumberInput label={t('quality.sample')} value={sample} min={0} decimalScale={3}
            onChange={(v) => setSample(typeof v === 'number' ? v : 1)} />
        </Group>
        <Group grow>
          <Select label={t('quality.resultCol')} value={result} onChange={(v) => setResult(v ?? '2')} data={[
            { value: '1', label: t('quality.result.pending') },
            { value: '2', label: t('quality.result.accepted') },
            { value: '3', label: t('quality.result.rejected') },
          ]} />
          <DatePickerInput label={t('quality.date')} value={date} onChange={setDate} />
        </Group>
        <TextInput label={t('quality.inspector')} value={inspector} onChange={(e) => setInspector(e.currentTarget.value)} />
        <Textarea label={t('quality.notes')} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} autosize minRows={2} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
          <Button color="wheels" loading={save.isPending} disabled={!item.trim()} onClick={() => save.mutate()}>{t('common.save')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
